import { useEffect, useState, useRef, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  FileText, ScanText, Loader2, AlertTriangle, CheckCircle2,
  Package, FileWarning, BarChart3, RefreshCw, ExternalLink,
  Wrench, LifeBuoy, Plus, Check, ChevronDown, ChevronRight,
  Receipt, FilePlus2, FileMinus, ScrollText, Upload, AlertCircle,
  Sparkles,
} from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";
import ConfirmModal from "../ConfirmModal";
import Modal from "../Modal";
import InlineUploadPanel from "../InlineUploadPanel";
import { DocRow, PaymentModal } from "./AccountBlock";
import EquipmentExtractModal from "./EquipmentExtractModal";

/* ────────────────────────── Section catalogue ──────────────────────────── */
/* Unified documents view: OCR-scanned docs AND manually-uploaded sales docs
 * live together in the same per-type fold. The old "Account" grid card has
 * been absorbed into these folds so there's no duplication.
 *
 *   sectionKey       OCR doc_type          sales_docs category
 *   ─────────────    ──────────────        ───────────────────
 *   invoice_out      invoice_out           invoice
 *   sales_order      (none — manual only)  sales_order
 *   credit_note      credit_note           credit_note
 *   statement        statement             statement
 *   quote            quote                 quote
 */
const SECTIONS = [
  {
    key: "invoice_out",
    label: "Invoices",
    icon: Receipt,
    color: "#F59E0B",
    salesCategory: "invoice",
    ocrType: "invoice_out",
    emptyHint: "Scan an invoice you issued to this customer, or upload a PDF.",
  },
  {
    key: "sales_order",
    label: "Sales orders",
    icon: ScrollText,
    color: "#1FB6C1",
    salesCategory: "sales_order",
    // OCR classifies these as "quote" (Vision frequently swaps SOs/quotes);
    // we route both to this section when a manual SO exists. See grouping below.
    ocrType: "sales_order",
    emptyHint: "Scan or upload sales orders / proformas for this customer.",
  },
  {
    key: "credit_note",
    label: "Credit notes",
    icon: FileMinus,
    color: "#F472B6",
    salesCategory: "credit_note",
    ocrType: "credit_note",
    emptyHint: "Scan or upload credit notes issued against this customer.",
  },
  {
    key: "statement",
    label: "Statements",
    icon: FileWarning,
    color: "#10B981",
    salesCategory: "statement",
    ocrType: "statement",
    emptyHint: "Scan the customer's latest statement to reconcile.",
  },
  {
    key: "quote",
    label: "Quotes",
    icon: FilePlus2,
    color: "#A78BFA",
    salesCategory: "quote",
    ocrType: "quote",
    emptyHint: "Scan or upload quotes issued to this customer.",
  },
  // Iter 425.c — Customer-side purchase orders (customer POs sent TO us).
  // No DNU split (customer docs only). Otherwise identical workflow to
  // quotes / sales orders — Scan/Upload/Edit/Delete/Link-to-project.
  {
    key: "purchase_order",
    label: "Purchase orders",
    icon: FileText,
    color: "#7C3AED",
    salesCategory: "purchase_order",
    ocrType: "purchase_order",
    emptyHint: "Scan or upload customer purchase orders received from this account.",
  },
];

const CHIP_MATRIX = {
  invoice_out: ["equipment", "analysis", "jobcard", "ticket"],
  credit_note: ["equipment", "analysis", "ticket"],
  statement: ["statement", "ticket"],
  quote: ["jobcard", "ticket"],
  sales_order: ["jobcard", "ticket"],
};

/* ─────────────────────────── Root block ──────────────────────────────── */

export default function DocumentsBlock({ customer, canEdit, salesDocs = [], onChanged, siteId = null }) {
  const nav = useNavigate();
  const [ocrDocs, setOcrDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  // Wave 29 Chunk 8e — upload/edit moved to full-page routes
  // (DESIGN_CONTRACT Rule 0). Payment dialog is single-action and stays.
  const [payTarget, setPayTarget] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [delBusy, setDelBusy] = useState(false);

  // Bulk-select state (Phase 5b) — set of OCR doc ids selected for bulk actions.
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  // Pickers shared by bulk bar + single-row flows.
  const [bulkJcPickerOpen, setBulkJcPickerOpen] = useState(false);
  const [bulkTicketPickerOpen, setBulkTicketPickerOpen] = useState(false);

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/ocr/customer-documents", {
        params: { customer_id: customer.id },
      });
      setOcrDocs(Array.isArray(data) ? data : []);
    } catch {
      setOcrDocs([]);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    if (customer?.id) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer?.id]);

  // When rendered on SiteDetail, drop everything not tied to this site.
  const filteredOcr = useMemo(
    () => {
      const list = Array.isArray(ocrDocs) ? ocrDocs : [];
      return siteId ? list.filter((d) => d.site_id === siteId) : list;
    },
    [ocrDocs, siteId],
  );
  const filteredManual = useMemo(
    () => {
      const list = Array.isArray(salesDocs) ? salesDocs : [];
      return siteId ? list.filter((d) => d.site_id === siteId) : list;
    },
    [salesDocs, siteId],
  );

  const grouped = useMemo(() => {
    const by = {};
    SECTIONS.forEach((s) => (by[s.key] = { ocr: [], manual: [], ocrBySalesDoc: {} }));
    for (const d of filteredOcr) {
      const target = SECTIONS.find((s) => s.ocrType === d.doc_type);
      if (!target) continue;
      by[target.key].ocr.push(d);
      if (d.source_sales_document_id) {
        by[target.key].ocrBySalesDoc[d.source_sales_document_id] = d;
      }
    }
    for (const d of filteredManual) {
      const target = SECTIONS.find((s) => s.salesCategory === d.category);
      if (target) by[target.key].manual.push(d);
      if (target) {
        const linkedOcr = filteredOcr.find(
          (o) => o.source_sales_document_id === d.id,
        );
        if (linkedOcr) {
          by[target.key].ocrBySalesDoc[d.id] = linkedOcr;
        }
      }
    }
    return by;
  }, [filteredOcr, filteredManual]);

  const reloadAll = async () => {
    await load();
    onChanged?.();
  };

  // All selected OCR docs (keeping order of `filteredOcr`).
  const selectedDocs = useMemo(
    () => filteredOcr.filter((d) => selectedIds.has(d.id)),
    [filteredOcr, selectedIds],
  );

  // For a given chip, which selected docs are eligible + currently pending?
  const eligibleFor = (chip) => {
    return selectedDocs.filter((d) => {
      if (d.status === "processing" || d.status === "failed") return false;
      const applicable = (CHIP_MATRIX[d.doc_type] || []).includes(chip);
      if (!applicable) return false;
      if ((chip === "equipment" || chip === "jobcard") && (d.lines || []).length === 0) return false;
      const cur = d.actions?.[chip]?.status;
      return cur !== "applied";
    });
  };

  const bulkApply = async (chip, extraBody = {}) => {
    const targets = eligibleFor(chip);
    if (targets.length === 0) return;
    setBulkBusy(true);
    setErr("");
    let ok = 0;
    const failed = [];
    const endpoint = {
      equipment: (d) => ({ url: `/ocr/invoice/${d.id}/apply`, body: {} }),
      analysis: (d) => ({ url: `/ocr/invoice/${d.id}/apply-analysis`, body: {} }),
      statement: (d) => ({ url: `/ocr/invoice/${d.id}/apply-statement`, body: {} }),
      jobcard: (d) => ({ url: `/ocr/invoice/${d.id}/apply-jobcard`, body: extraBody }),
      ticket: (d) => ({ url: `/ocr/invoice/${d.id}/apply-ticket`, body: extraBody }),
    }[chip];
    // Fire sequentially to avoid racing the same backend resource.
    for (const doc of targets) {
      try {
        const { url, body } = endpoint(doc);
        await api.post(url, body);
        ok += 1;
      } catch (ex) {
        failed.push(`#${(doc.invoice_number || doc.id.slice(0, 6))}`);
      }
    }
    setBulkBusy(false);
    setBulkJcPickerOpen(false);
    setBulkTicketPickerOpen(false);
    if (failed.length) setErr(`${ok} applied · ${failed.length} failed: ${failed.join(", ")}`);
    await reloadAll();
    // Keep selection so the user can spot what succeeded (ticks appear).
  };

  const delDoc = async () => {
    if (!confirmDel) return;
    setDelBusy(true);
    try {
      await api.delete(`/sales-documents/${confirmDel.id}`);
      setConfirmDel(null);
      onChanged?.();
    } finally {
      setDelBusy(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="documents-block">
      {err && (
        <div className="text-xs text-rose-300 border border-rose-500/40 bg-rose-500/10 rounded-md p-2 flex items-center gap-2">
          <AlertTriangle className="w-3.5 h-3.5" /> {err}
        </div>
      )}

      {canEdit && selectedIds.size > 0 && (
        <BulkActionBar
          selectedDocs={selectedDocs}
          eligibleFor={eligibleFor}
          busy={bulkBusy}
          onApply={bulkApply}
          onOpenJcPicker={() => setBulkJcPickerOpen(true)}
          onOpenTicketPicker={() => setBulkTicketPickerOpen(true)}
          onClear={clearSelection}
        />
      )}

      {SECTIONS.map((section) => (
        <DocSection
          key={section.key}
          section={section}
          ocrDocs={grouped[section.key].ocr}
          manualDocs={grouped[section.key].manual}
          ocrBySalesDoc={grouped[section.key].ocrBySalesDoc}
          customer={customer}
          canEdit={canEdit}
          onUploadManual={() => nav(`/customers/${customer.id}/sales-docs/new?category=${section.salesCategory}`)}
          onEditManual={(d) => nav(`/sales-documents/${d.id}/edit`)}
          onDeleteManual={setConfirmDel}
          onPayManual={setPayTarget}
          onOcrChanged={reloadAll}
          onError={setErr}
          refreshing={loading}
          onRefresh={load}
          selectedIds={selectedIds}
          onToggleSelect={toggleSelect}
        />
      ))}

      {/* Wave 29 Chunk 8e — upload + edit moved to full-page routes.
          Only payment + delete confirms remain as small modals (single
          action — allowed by DESIGN_CONTRACT Rule 0). */}
      {payTarget && (
        <PaymentModal
          doc={payTarget}
          onClose={() => setPayTarget(null)}
          onSaved={() => {
            setPayTarget(null);
            onChanged?.();
          }}
        />
      )}
      <ConfirmModal
        open={!!confirmDel}
        onClose={() => setConfirmDel(null)}
        onConfirm={delDoc}
        title="Delete document"
        message={
          <>
            Permanently delete <b>{confirmDel?.title}</b>?
          </>
        }
        confirmLabel="Delete document"
        testId="sales-doc-delete-confirm"
        busy={delBusy}
      />

      {bulkJcPickerOpen && (
        <JobCardPicker
          customerId={customer.id}
          onClose={() => setBulkJcPickerOpen(false)}
          onPick={(body) => bulkApply("jobcard", body)}
          bulkCount={eligibleFor("jobcard").length}
        />
      )}
      {bulkTicketPickerOpen && (
        <TicketPicker
          customerId={customer.id}
          onClose={() => setBulkTicketPickerOpen(false)}
          onPick={(ticket_id) => bulkApply("ticket", { ticket_id })}
          bulkCount={eligibleFor("ticket").length}
        />
      )}
    </div>
  );
}

/* ──────────────────────── Bulk action bar ───────────────────────────────── */

function BulkActionBar({ selectedDocs, eligibleFor, busy, onApply, onOpenJcPicker, onOpenTicketPicker, onClear }) {
  const count = selectedDocs.length;
  const chips = [
    { key: "equipment", label: "Equipment", icon: Package, color: "#1FB6C1" },
    { key: "analysis",  label: "Analysis",  icon: BarChart3, color: "#A78BFA" },
    { key: "statement", label: "Apply balance", icon: FileWarning, color: "#F59E0B" },
    { key: "jobcard",   label: "Job card", icon: Wrench, color: "#EAB308", picker: onOpenJcPicker },
    { key: "ticket",    label: "Ticket",   icon: LifeBuoy, color: "#F43F5E", picker: onOpenTicketPicker },
  ];
  return (
    <div
      className="fixed left-1/2 -translate-x-1/2 bottom-4 md:bottom-6 z-40 w-[min(96vw,960px)] rounded-xl border p-3 shadow-2xl flex items-center gap-2 flex-wrap animate-in fade-in slide-in-from-bottom-4"
      style={{
        borderColor: "#1FB6C166",
        background: "linear-gradient(135deg, rgba(12,24,32,0.92) 0%, rgba(8,14,20,0.95) 100%)",
        backdropFilter: "blur(14px)",
        boxShadow: "0 10px 40px -10px rgba(31,182,193,0.35), 0 0 0 1px rgba(31,182,193,0.18)",
      }}
      data-testid="bulk-action-bar"
    >
      <div className="flex items-center gap-2 shrink-0">
        <div className="w-8 h-8 rounded-md bg-[#1FB6C1]/20 border border-[#1FB6C1]/50 flex items-center justify-center">
          <Check className="w-4 h-4 text-[#1FB6C1]" />
        </div>
        <div>
          <div className="text-xs uppercase tracking-wider text-[#1FB6C1] font-bold">
            {count} document{count === 1 ? "" : "s"} selected
          </div>
          <div className="text-[10px] text-[#a0aabf]">Apply an action to all eligible docs below.</div>
        </div>
      </div>
      <div className="flex items-center gap-1.5 flex-wrap flex-1 justify-end">
        {chips.map((c) => {
          const eligible = eligibleFor(c.key).length;
          const Icon = c.icon;
          return (
            <button
              key={c.key}
              type="button"
              disabled={busy || eligible === 0}
              onClick={() => (c.picker ? c.picker() : onApply(c.key))}
              className="text-xs px-2.5 py-1.5 rounded border flex items-center gap-1.5 transition hover:scale-[1.03] disabled:opacity-40 disabled:hover:scale-100"
              style={{
                borderColor: `${c.color}66`,
                background: `${c.color}14`,
                color: c.color,
              }}
              data-testid={`bulk-chip-${c.key}`}
              title={eligible === 0 ? "None of the selected docs are eligible for this action" : `${eligible} of ${count} eligible`}
            >
              {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Icon className="w-3 h-3" />}
              {c.label}
              <span className="text-[10px] font-mono opacity-70">({eligible})</span>
            </button>
          );
        })}
        <button
          type="button"
          onClick={onClear}
          className="hv-btn hv-btn-ghost text-xs"
          data-testid="bulk-clear"
        >
          <AlertCircle className="w-3 h-3" /> Clear
        </button>
      </div>
    </div>
  );
}

/* ────────────────────────── Section (one fold) ──────────────────────────── */

function DocSection({
  section, ocrDocs, manualDocs, ocrBySalesDoc, customer, canEdit,
  onUploadManual, onEditManual, onDeleteManual, onPayManual,
  onOcrChanged, onError, refreshing, onRefresh,
  selectedIds, onToggleSelect,
}) {
  const [open, setOpen] = useState(false);
  const [scanBusy, setScanBusy] = useState(false);
  // Iter 290r — null = closed, 'upload' = PC file picker first, 'scan'
  // = mobile camera first. Both routes use the same InlineUploadPanel
  // and the same /sales-documents/bulk-upload backend.
  const [uploadMode, setUploadMode] = useState(null);
  // Wave 29 Chunk 8f.2 — clickable DNU/CF filter (Dirk 2026-05-11).
  // null = show both; "internal" = only DNU; "customer" = only CF.
  // Only applies to quote + sales_order sections; other sections ignore it.
  const [audienceFilter, setAudienceFilter] = useState(null);
  const fileRef = useRef(null);
  const Icon = section.icon;

  // Iter 290r — totalCount no longer double-counts OCR records paired
  // to a manual sales_document. A paired OCR record IS that manual doc;
  // we show it inline. Only ORPHAN OCR rows (no source_sales_document_id)
  // get their own row + their own count.
  const orphanOcrDocs = ocrDocs.filter((d) => !d.source_sales_document_id);
  const totalCount = orphanOcrDocs.length + manualDocs.length;

  const pendingCount = useMemo(() => {
    // Pending chips on unpaired OCR docs + on OCR jobs linked to manual rows.
    const unpairedPending = ocrDocs.filter(
      (d) => !d.source_sales_document_id && rowHasPendingChip(d, section.key),
    ).length;
    const linkedPending = Object.values(ocrBySalesDoc).filter(
      (d) => rowHasPendingChip(d, section.key),
    ).length;
    return unpairedPending + linkedPending;
  }, [ocrDocs, ocrBySalesDoc, section.key]);
  const outstandingInvoices = useMemo(
    () => (section.salesCategory === "invoice"
      ? manualDocs.filter((d) => d.payment_status === "outstanding").length
      : 0),
    [manualDocs, section.salesCategory],
  );

  const totalValue = useMemo(
    () => {
      // Iter 365 (2026-02-27) — Dirk: "It shows nine hundred and three
      // thousand, but that's not what's outstanding. The only invoices
      // outstanding is about two hundred and seventeen." For the
      // invoice section we now sum ONLY unpaid invoices (payment_status
      // in outstanding/overdue). For statements we surface the LATEST
      // statement's closing balance because every statement rolls up
      // its predecessors — summing them would triple-count. Quotes +
      // sales orders keep the running total (informational).
      const cat = section.salesCategory;
      if (cat === "invoice") {
        const unpaid = (d) => {
          const s = (d.payment_status || "").toLowerCase();
          return s === "outstanding" || s === "overdue";
        };
        const manualUnpaid = manualDocs.filter(unpaid).reduce(
          (s, d) => s + (Number(d.amount) || 0), 0,
        );
        const ocrUnpaid = orphanOcrDocs
          .filter((d) => {
            const s = (d.payment_status || d.invoice?.payment_status || "").toLowerCase();
            return s === "outstanding" || s === "overdue" || !s;
          })
          .reduce((s, d) => s + (Number(d.totals?.total) || 0), 0);
        return manualUnpaid + ocrUnpaid;
      }
      if (cat === "statement") {
        // Latest by statement_date (fall back to created_at). Show
        // ONLY that statement's closing balance / total_due — old
        // statements are historical snapshots.
        const stmtDocs = [...manualDocs, ...orphanOcrDocs];
        if (stmtDocs.length === 0) return 0;
        const dateOf = (d) =>
          d.statement?.statement_date || d.statement_date || d.doc_date ||
          d.created_at || "";
        const latest = stmtDocs.reduce((best, d) => {
          const dt = dateOf(d);
          return !best || dt > dateOf(best) ? d : best;
        }, null);
        if (!latest) return 0;
        const bal =
          latest.statement?.closing_balance ??
          latest.statement?.total_due ??
          Number(latest.amount) ??
          Number(latest.totals?.total) ?? 0;
        return Number(bal) || 0;
      }
      // Quotes + sales orders + credit notes: running total across all
      // rows stays useful.
      return manualDocs.reduce((s, d) => s + (Number(d.amount) || 0), 0)
        // Iter 290r — only sum ORPHAN OCR docs. Paired OCR records
        // belong to a manual sales_document and would double-count.
        + orphanOcrDocs.reduce((s, d) => s + (Number(d.totals?.total) || 0), 0);
    },
    [orphanOcrDocs, manualDocs, section.salesCategory],
  );

  const onScan = async (fileList) => {
    const files = Array.from(fileList || []).filter(Boolean);
    if (files.length === 0) return;
    if (!section.salesCategory) {
      onError(`Drop is not enabled for this section (${section.label}).`);
      return;
    }
    // Iter 290zp.15z05 — Drag-drop respects the section's audience
    // filter chip (DNU/CF) when set. For categories that allow a
    // pick (quote / sales_order / invoice), refuse the drop when no
    // filter is active and prompt the rep to either click DNU/CF
    // first OR use the audience-first upload panel. credit_note +
    // statement remain customer-only — no picker needed.
    const auPickCats = ["quote", "sales_order", "invoice"];
    let audience;
    if (auPickCats.includes(section.salesCategory)) {
      if (audienceFilter === "internal" || audienceFilter === "customer") {
        audience = audienceFilter;
      } else {
        onError(
          `Click the DNU or CF chip above first so I know which side this ${section.label.toLowerCase().replace(/s$/, "")} belongs to — or use the Upload button for the full picker.`,
        );
        return;
      }
    } else {
      audience = "customer";
    }
    setScanBusy(true);
    onError("");
    // Iter 290zp.15z21 — Was: per-file POST /ocr/invoice?async=true with
    // image_base64 (huge JSON payloads, blocking serial loop, hardcoded
    // to /ocr/invoice regardless of section, no sales_document row
    // created until OCR finished — so the rep thought the drop was
    // lost). Now: single multipart POST to /sales-documents/bulk-upload
    // which (a) writes the file to R2, (b) creates a sales_documents
    // row with `ocr_status: "pending"` immediately so it shows in the
    // list, (c) schedules background OCR. Audience is now sourced from
    // the section's audienceFilter (see iter 290zp.15z05 above) rather
    // than hardcoded — so DNU + CF invoices land in their correct lane.
    try {
      const form = new FormData();
      form.append("customer_id", customer.id);
      form.append("category", section.salesCategory);
      form.append("audience", audience);
      for (const f of files) form.append("files", f, f.name);
      const { data } = await api.post(
        "/sales-documents/bulk-upload",
        form,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      const queued = data?.count ?? 0;
      setScanBusy(false);
      if (queued > 0) {
        await onOcrChanged();
      } else {
        onError("No files were accepted. Check file size + type.");
      }
    } catch (ex) {
      setScanBusy(false);
      onError(
        ex?.response?.data?.detail
        || ex?.message
        || "Upload failed — please try again.",
      );
    }
  };

  const [dragOver, setDragOver] = useState(false);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (!canEdit || !section.salesCategory) return;
    const files = e.dataTransfer?.files;
    if (files?.length) onScan(files);
  };

  return (
    <section
      className={`hv-card overflow-hidden transition ${dragOver ? "border-[#1FB6C1]/60 bg-[#1FB6C1]/5" : ""}`}
      data-testid={`documents-section-${section.key}`}
      data-fold-state={open ? "open" : "closed"}
      onDragOver={(e) => { if (canEdit && section.salesCategory) { e.preventDefault(); setDragOver(true); } }}
      onDragLeave={() => setDragOver(false)}
      onDrop={handleDrop}
    >
      <header
        className="px-4 md:px-5 py-3 flex items-center justify-between gap-3 cursor-pointer select-none hover:bg-white/[0.02] transition-colors"
        onClick={() => setOpen((v) => !v)}
        data-testid={`documents-section-${section.key}-toggle`}
      >
        <div className="flex items-center gap-3 min-w-0 flex-wrap">
          {open ? <ChevronDown className="w-4 h-4 text-[#647087] shrink-0" /> : <ChevronRight className="w-4 h-4 text-[#647087] shrink-0" />}
          <div
            className="w-8 h-8 rounded-md flex items-center justify-center border shrink-0"
            style={{ borderColor: `${section.color}40`, background: `${section.color}14`, color: section.color }}
          >
            <Icon className="w-4 h-4" />
          </div>
          <h3 className="font-display text-base md:text-lg font-bold tracking-tight truncate">{section.label}</h3>
          <span
            className={`text-[10px] uppercase tracking-wider font-mono px-2 py-0.5 rounded-full border shrink-0 ${
              totalCount
                ? "border-[#1fb6c1]/40 text-[#1fb6c1] bg-[#1fb6c1]/5"
                : "border-white/10 text-[#647087] bg-white/[0.02]"
            }`}
          >
            {totalCount}
          </span>
          {outstandingInvoices > 0 && (
            <span className="text-[10px] uppercase tracking-wider font-mono px-2 py-0.5 rounded-full border border-rose-400/50 text-rose-300 bg-rose-500/10 shrink-0 flex items-center gap-1">
              <AlertCircle className="w-2.5 h-2.5" /> {outstandingInvoices} due
            </span>
          )}
          {pendingCount > 0 && (
            <span
              className="text-[10px] uppercase tracking-wider font-mono px-2 py-0.5 rounded-full border border-amber-400/40 text-amber-300 bg-amber-400/10 shrink-0"
              title={`${pendingCount} doc${pendingCount === 1 ? "" : "s"} accepted but still need to be linked to a job card or ticket. The OCR is done — click into the doc to wire it up.`}
              data-testid={`section-${section.key}-tolink-badge`}
            >
              {pendingCount} to link
            </span>
          )}
          {totalValue > 0 && (
            <span
              className="text-[10px] uppercase tracking-wider font-mono px-2 py-0.5 rounded-full border border-emerald-500/40 text-emerald-300 bg-emerald-500/5 shrink-0 hidden md:inline-flex"
              title={
                section.salesCategory === "invoice"
                  ? "Sum of outstanding + overdue invoices only"
                  : section.salesCategory === "statement"
                  ? "Balance from the latest statement (rolls up historic)"
                  : "Sum of all documents in this section"
              }
              data-testid={`section-${section.key}-total`}
            >
              R {Math.round(totalValue).toLocaleString('en-GB')}
              {section.salesCategory === "invoice" && (
                <span className="ml-1 opacity-70">due</span>
              )}
              {section.salesCategory === "statement" && (
                <span className="ml-1 opacity-70">latest</span>
              )}
            </span>
          )}
          {/* Wave 29 Chunk 8f.1 — DNU/CF count chips on quote + sales_order
              section headers (Dirk 2026-05-11: "should customers page not
              split sales order and qte into dnu and cf now?"). Visible even
              when the section is collapsed. */}
          {/* Iter 290zp.15z04 — section-header DNU/CF chips for every
              audience-split category (quote, sales_order, invoice).
              User flagged "Invoices are not split into DNU and CF like
              sales orders" — the conditional was accidentally scoped to
              engineering categories only. credit_note + statement stay
              flat because the backend forces them to audience=customer. */}
          {["quote", "sales_order", "invoice"].includes(section.salesCategory) && (() => {
            const dnuN = manualDocs.filter((d) => (d.audience || "customer") === "internal").length;
            const cfN  = manualDocs.length - dnuN;
            const onChip = (e, newFilter) => {
              // Stop propagation so clicking a chip doesn't toggle the
              // <header> open/close (the parent <header> has onClick).
              e.stopPropagation();
              setAudienceFilter((cur) => (cur === newFilter ? null : newFilter));
              if (!open) setOpen(true); // auto-expand on filter
            };
            const dnuActive = audienceFilter === "internal";
            const cfActive  = audienceFilter === "customer";
            return (
              <>
                <button
                  type="button"
                  onClick={(e) => onChip(e, "internal")}
                  className={`text-[9px] uppercase tracking-wider font-mono px-1.5 py-0.5 rounded border shrink-0 transition ${
                    dnuActive
                      ? "border-rose-300 text-rose-100 bg-rose-500/30 shadow-[0_0_0_2px_rgba(244,63,94,.25)]"
                      : "border-rose-400/40 text-rose-300 bg-rose-500/10 hover:bg-rose-500/20"
                  }`}
                  title={dnuActive ? "Showing only internal — click to clear filter" : "Tap to show ONLY internal (DNU) docs"}
                  data-testid={`section-chip-dnu-${section.salesCategory}`}
                  aria-pressed={dnuActive}
                >
                  DNU {dnuN}
                </button>
                <button
                  type="button"
                  onClick={(e) => onChip(e, "customer")}
                  className={`text-[9px] uppercase tracking-wider font-mono px-1.5 py-0.5 rounded border shrink-0 transition ${
                    cfActive
                      ? "border-emerald-300 text-emerald-100 bg-emerald-500/30 shadow-[0_0_0_2px_rgba(16,185,129,.25)]"
                      : "border-emerald-400/40 text-emerald-300 bg-emerald-500/10 hover:bg-emerald-500/20"
                  }`}
                  title={cfActive ? "Showing only customer-facing — click to clear filter" : "Tap to show ONLY customer-facing docs"}
                  data-testid={`section-chip-cf-${section.salesCategory}`}
                  aria-pressed={cfActive}
                >
                  CF {cfN}
                </button>
              </>
            );
          })()}
        </div>
        <div className="flex items-center gap-1 shrink-0" onClick={(e) => e.stopPropagation()}>
          {canEdit && (
            <>
              <button
                type="button"
                onClick={() => { setUploadMode("scan"); if (!open) setOpen(true); }}
                className="hv-btn hv-btn-ghost px-2 py-1 text-xs"
                data-testid={`documents-section-${section.key}-scan`}
                title="Scan with phone camera"
              >
                <ScanText className="w-3 h-3" />
                <span className="hidden sm:inline">Scan</span>
              </button>
              <button
                type="button"
                onClick={() => { setUploadMode("upload"); if (!open) setOpen(true); }}
                className="hv-btn hv-btn-ghost px-2 py-1 text-xs"
                data-testid={`documents-section-${section.key}-upload`}
                title="Upload from this device"
              >
                <Upload className="w-3 h-3" />
                <span className="hidden sm:inline">Upload</span>
              </button>
            </>
          )}
        </div>
      </header>

      {/* Iter 290r — inline audience-first upload / scan panel. Replaces
          the old SalesDocScanner+SalesDocEditor flow that was a UX trap
          ("Drop into top section → sync OCR → audience never honored").
          Now: pick DNU/CF first, then drop file, instant upload, OCR
          runs async on the bulk-upload endpoint. */}
      {open && uploadMode && section.salesCategory && (
        <div className="px-4 md:px-5 pt-3 border-t border-white/5">
          <InlineUploadPanel
            customerId={customer.id}
            category={section.salesCategory}
            defaultCapture={uploadMode === "scan"}
            onClose={() => setUploadMode(null)}
            onUploaded={async () => {
              setUploadMode(null);
              await onOcrChanged();
            }}
          />
        </div>
      )}

      {open && (
        <div className="px-4 md:px-5 pb-4 pt-1 border-t border-white/5 space-y-2">
          {totalCount === 0 ? (
            <div className="text-center text-xs text-[#647087] italic py-4">
              {section.emptyHint}
            </div>
          ) : (
            <>
              {/* Manual (sales_documents) rows — each with its own OCR scan/chips strip */}
              {manualDocs.length > 0 && (
                ["quote", "sales_order", "invoice"].includes(section.salesCategory) ? (
                  // Wave 29 Chunk 8e — engineering categories get a hard
                  // DNU / CF visual split here too (mirrors AccountBlock).
                  // Wave 29 Chunk 8f.2 — section header chips can filter
                  // the visible side; when filtered, the OTHER side is
                  // hidden entirely.
                  // Iter 290zp.15z04 — extended to invoice (financial
                  // doc now mirrors the engineering split so the
                  // equipment-extraction chip story is honest).
                  // credit_note + statement stay flat (always customer).
                  (() => {
                    const sorted = manualDocs.slice().sort(
                      (a, b) => new Date(b.document_date || b.created_at) - new Date(a.document_date || a.created_at),
                    );
                    const internal = sorted.filter((d) => (d.audience || "customer") === "internal");
                    const customerDocs = sorted.filter((d) => (d.audience || "customer") !== "internal");
                    const showInternal = audienceFilter == null || audienceFilter === "internal";
                    const showCustomer = audienceFilter == null || audienceFilter === "customer";
                    const renderRow = (d) => (
                      <ManualRowWithOcr
                        key={`manual-${d.id}`}
                        manualDoc={d}
                        linkedOcr={ocrBySalesDoc[d.id]}
                        accent={section.color}
                        canEdit={canEdit}
                        customerId={customer.id}
                        onEdit={() => onEditManual(d)}
                        onDelete={() => onDeleteManual(d)}
                        onPay={() => onPayManual(d)}
                        onChanged={onOcrChanged}
                        onError={onError}
                        selectedIds={selectedIds}
                        onToggleSelect={onToggleSelect}
                      />
                    );
                    return (
                      <div data-testid={`account-audience-split-${section.salesCategory}`} className="space-y-2">
                        {audienceFilter && (
                          <div className="px-3 py-1 text-[10px] text-amber-300 bg-amber-500/10 border border-amber-400/30 rounded inline-flex items-center gap-2" data-testid={`section-filter-active-${section.salesCategory}`}>
                            <span>Filtering: <b>{audienceFilter === "internal" ? "Internal (DNU)" : "Customer-facing (CF)"}</b></span>
                            <button type="button" onClick={() => setAudienceFilter(null)} className="underline hover:no-underline">
                              Clear
                            </button>
                          </div>
                        )}
                        {showInternal && (
                        <div className="border border-rose-500/15 rounded-md overflow-hidden">
                          <div
                            className="px-3 py-1.5 bg-rose-500/5 border-b border-rose-500/15 flex items-center justify-between"
                            data-testid={`account-section-dnu-${section.salesCategory}`}
                          >
                            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-rose-300 inline-flex items-center gap-1.5">
                              Internal · DNU
                              <span className="text-[9px] font-mono px-1 border border-rose-400/40 rounded">{internal.length}</span>
                            </div>
                            <span className="text-[9px] text-[#647087] italic">Engineering — never shared with customer</span>
                          </div>
                          <div className="divide-y divide-white/5">
                            {internal.length === 0 ? (
                              <div className="px-3 py-2 text-[10px] text-[#647087] italic">
                                No internal {section.label.toLowerCase()} yet — upload your detailed DNU version to power project budgets + procurement.
                              </div>
                            ) : internal.map(renderRow)}
                          </div>
                        </div>
                        )}
                        {showCustomer && (
                        <div className="border border-emerald-500/15 rounded-md overflow-hidden">
                          <div
                            className="px-3 py-1.5 bg-emerald-500/5 border-b border-emerald-500/15 flex items-center justify-between"
                            data-testid={`account-section-cf-${section.salesCategory}`}
                          >
                            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-emerald-300 inline-flex items-center gap-1.5">
                              Customer · CF
                              <span className="text-[9px] font-mono px-1 border border-emerald-400/40 rounded">{customerDocs.length}</span>
                            </div>
                            <span className="text-[9px] text-[#647087] italic">Safe to share via portal</span>
                          </div>
                          <div className="divide-y divide-white/5">
                            {customerDocs.length === 0 ? (
                              <div className="px-3 py-2 text-[10px] text-[#647087] italic">
                                No customer-facing {section.label.toLowerCase()} yet.
                              </div>
                            ) : customerDocs.map(renderRow)}
                          </div>
                        </div>
                        )}
                      </div>
                    );
                  })()
                ) : (
                  <div className="border border-white/5 rounded-md divide-y divide-white/5 overflow-hidden">
                    {manualDocs
                      .slice()
                      .sort((a, b) => new Date(b.document_date || b.created_at) - new Date(a.document_date || a.created_at))
                      .map((d) => (
                        <ManualRowWithOcr
                          key={`manual-${d.id}`}
                          manualDoc={d}
                          linkedOcr={ocrBySalesDoc[d.id]}
                          accent={section.color}
                          canEdit={canEdit}
                          customerId={customer.id}
                          onEdit={() => onEditManual(d)}
                          onDelete={() => onDeleteManual(d)}
                          onPay={() => onPayManual(d)}
                          onChanged={onOcrChanged}
                          onError={onError}
                          selectedIds={selectedIds}
                          onToggleSelect={onToggleSelect}
                        />
                      ))}
                  </div>
                )
              )}

              {/* Standalone OCR rows — those NOT paired to a manual upload.
                  After Wave 7 #5, every new OCR ingest auto-creates a paired
                  sales_documents row, so this list only contains LEGACY
                  orphans (created before that auto-materialization shipped).
                  We keep them visible but visually de-emphasised so the user
                  can still see chip state on old data. */}
              {(() => {
                const unpaired = ocrDocs.filter((d) => !d.source_sales_document_id);
                if (unpaired.length === 0) return null;
                return (
                  <details className="group" data-testid={`documents-section-${section.key}-legacy`}>
                    <summary className="text-[10px] uppercase tracking-wider text-[#647087] cursor-pointer hover:text-[#a0aabf] flex items-center gap-1 pt-2 pl-1">
                      <ChevronRight className="w-3 h-3 transition-transform group-open:rotate-90" />
                      Legacy unlinked scans ({unpaired.length})
                    </summary>
                    <div className="space-y-1.5 pt-1.5">
                      {unpaired.map((d) => (
                        <OcrRow
                          key={`ocr-${d.id}`}
                          doc={d}
                          customerId={customer.id}
                          onChanged={onOcrChanged}
                          onError={onError}
                          selected={selectedIds?.has(d.id)}
                          onToggleSelect={onToggleSelect}
                        />
                      ))}
                    </div>
                  </details>
                );
              })()}
            </>
          )}
          <div className="flex items-center justify-end pt-1">
            <button
              type="button"
              onClick={onRefresh}
              disabled={refreshing}
              className="hv-btn hv-btn-ghost text-[10px]"
              data-testid={`documents-section-${section.key}-refresh`}
            >
              <RefreshCw className={`w-3 h-3 ${refreshing ? "animate-spin" : ""}`} /> Refresh
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

/* ───────────── Manual row wrapped with OCR scan/chip strip ────────────── */

function ManualRowWithOcr({
  manualDoc, linkedOcr, accent, canEdit, customerId,
  onEdit, onDelete, onPay, onChanged, onError,
  selectedIds, onToggleSelect,
}) {
  const [scanBusy, setScanBusy] = useState(false);
  const [expanded, setExpanded] = useState(false);
  // Iter 290zp.14 — Equipment extraction modal opened from chip on
  // invoice / credit_note rows. Pulls line_items from this manual doc
  // (already populated by background OCR) and creates equipment rows
  // tagged with source_sales_document_id provenance.
  const [equipModalOpen, setEquipModalOpen] = useState(false);
  const [equipBusy, setEquipBusy] = useState(false);
  const [confirmUnsync, setConfirmUnsync] = useState(false);

  const isEquipmentEligible =
    manualDoc.category === "invoice" || manualDoc.category === "credit_note";
  const hasOcrLines = (manualDoc.line_items || []).length > 0;
  const equipmentApplied = !!manualDoc.equipment_applied;
  const equipmentCount = manualDoc.equipment_count || 0;

  const unsyncEquipment = async () => {
    setEquipBusy(true);
    onError("");
    try {
      await api.delete(`/sales-documents/${manualDoc.id}/equipment/unsync`);
      await onChanged?.();
    } catch (ex) {
      onError(ex?.response?.data?.detail || "Unsync failed.");
    } finally {
      setEquipBusy(false);
      setConfirmUnsync(false);
    }
  };

  const handleScan = async () => {
    setScanBusy(true);
    onError("");
    try {
      await api.post(`/ocr/invoice/from-sales-document/${manualDoc.id}`);
      await onChanged?.();
    } catch (ex) {
      // Iter 410.3 — Big docs (40+ lines / 8+ pages) can outlast the
      // proxy's 60s HTTP timeout even when the backend job is still
      // running fine. Before assuming failure, peek at the current
      // ocr_status on the document. If it's still pending / running
      // OR the polling shows it flipped to `ready` while we were
      // waiting, show a soft "still scanning" state — not a scary
      // "OCR failed" banner that makes users re-upload.
      const detail = ex?.response?.data?.detail;
      const httpStatus = ex?.response?.status;
      const looksLikeTimeout =
        !httpStatus || httpStatus === 502 || httpStatus === 504 || httpStatus === 520;
      try {
        const { data: fresh } = await api.get(`/sales-documents/${manualDoc.id}`);
        const s = fresh?.ocr_status;
        if (s === "ready") {
          await onChanged?.();  // extraction already done — refresh UI
        } else if (looksLikeTimeout && (s === "pending" || s === "running" || s === "queued")) {
          onError("Still scanning in the background — this can take a few minutes on large docs. Refresh in a moment.");
          // Fire a delayed refresh so the UI updates once done.
          setTimeout(() => onChanged?.(), 20000);
        } else {
          onError(detail || "OCR failed — try re-uploading the PDF.");
        }
      } catch {
        onError(detail || "OCR failed — try re-uploading the PDF.");
      }
    } finally {
      setScanBusy(false);
    }
  };

  return (
    <div className="bg-white/[0.01]">
      {/* Reuse the standard DocRow for the top line — keeps download / edit / delete / mark-paid parity. */}
      <ul>
        <DocRow
          doc={manualDoc}
          accent={accent}
          canEdit={canEdit}
          canDelete={canEdit}
          canMarkPaid={canEdit}
          outstanding={manualDoc.payment_status === "outstanding"}
          paid={manualDoc.payment_status === "paid"}
          onEdit={onEdit}
          onDelete={onDelete}
          onMarkPayment={onPay}
          onAudienceChanged={onChanged}
        />
      </ul>

      {/* OCR action strip — either a Scan button OR the chip row when OCR'd. */}
      {canEdit && (
        <div
          className="px-3 py-1.5 border-t border-white/5 bg-white/[0.015] flex flex-wrap items-center gap-1.5"
          data-testid={`manual-ocr-strip-${manualDoc.id}`}
        >
          {/* Iter 290zp.14 — Equipment chip on every invoice / credit_note
              manual row, regardless of legacy OCR-job linkage. Powered by
              the sales_documents.line_items pipeline (background OCR or
              manual edit). */}
          {isEquipmentEligible && (
            <EquipmentChip
              applied={equipmentApplied}
              count={equipmentCount}
              busy={equipBusy}
              hasLines={hasOcrLines}
              docId={manualDoc.id}
              onOpen={() => setEquipModalOpen(true)}
              onUnsync={() => setConfirmUnsync(true)}
            />
          )}
          {linkedOcr ? (
            <>
              {/* Iter 411 — SELECT checkbox removed per user request. The
                  bulk-select flow was confusing and rarely used. Bulk
                  actions can be re-introduced later behind a "Bulk mode"
                  toggle if needed. */}
              <button
                type="button"
                onClick={() => setExpanded((v) => !v)}
                className="text-[9px] uppercase tracking-wider text-emerald-300 font-bold flex items-center gap-1 hover:text-emerald-200"
                title="Toggle extracted detail"
                data-testid={`manual-ocr-toggle-${manualDoc.id}`}
              >
                <Sparkles className="w-3 h-3" /> OCR'd
                {(linkedOcr.lines || []).length > 0 && (
                  <span className="text-[#647087]">· {linkedOcr.lines.length} line{linkedOcr.lines.length !== 1 ? "s" : ""}</span>
                )}
                {expanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
              </button>
              <RowChips
                doc={linkedOcr}
                onChanged={onChanged}
                onError={onError}
                customerId={customerId}
              />
            </>
          ) : Array.isArray(manualDoc?.line_items) && manualDoc.line_items.length > 0 ? (
            // Iter 411 — Stale-state fix. The sales_document already has
            // OCR-extracted line_items, but the linked ocr_documents
            // record was cleaned up (retention pass, tenant migration,
            // etc.). Deep-link to the unified detail view so the operator
            // can view every extracted line + reconciliation totals.
            // Previously this rendered as a static "Scan with OCR"
            // fallback button that confused users (Dirk, 2026-02-28).
            <Link
              to={`/sales-documents/${manualDoc.id}/edit`}
              className="inline-flex items-center gap-1 text-[9px] uppercase tracking-wider text-emerald-300 font-bold hover:text-emerald-200 hover:underline"
              title={`OCR previously extracted ${manualDoc.line_items.length} line${manualDoc.line_items.length === 1 ? "" : "s"}. Click to view the full extracted table.`}
              data-testid={`manual-ocr-orphan-open-${manualDoc.id}`}
            >
              <Sparkles className="w-3 h-3" />
              {manualDoc.line_items.length} extracted line{manualDoc.line_items.length === 1 ? "" : "s"}
              <ChevronRight className="w-3 h-3" />
            </Link>
          ) : (
            <button
              type="button"
              onClick={handleScan}
              disabled={scanBusy}
              className="inline-flex items-center gap-1 text-[10px] font-medium uppercase tracking-wider px-2 py-1 rounded-md border border-white/10 text-[#a0aabf] hover:border-cyan-400/40 hover:text-cyan-300 transition-colors disabled:opacity-60"
              data-testid={`manual-ocr-scan-${manualDoc.id}`}
              title="Run OCR on this PDF so you can route line items to equipment / analytics / job card / ticket"
            >
              {scanBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
              {scanBusy ? "Scanning with OCR…" : "Scan with OCR"}
            </button>
          )}
        </div>
      )}

      {expanded && linkedOcr && (
        <div className="border-t border-white/5">
          <OcrDetail doc={linkedOcr} customerId={customerId} />
        </div>
      )}

      {/* Iter 290zp.14 — Equipment extraction modal + unsync confirm. */}
      {isEquipmentEligible && (
        <EquipmentExtractModal
          open={equipModalOpen}
          onClose={() => setEquipModalOpen(false)}
          documentId={manualDoc.id}
          documentLabel={
            manualDoc.document_number
              ? `${manualDoc.category === "credit_note" ? "Credit note" : "Invoice"} #${manualDoc.document_number}`
              : manualDoc.title
          }
          customerId={customerId}
          defaultSiteId={manualDoc.equipment_site_id || manualDoc.site_id || null}
          onApplied={async () => {
            await onChanged?.();
          }}
        />
      )}
      <ConfirmModal
        open={confirmUnsync}
        onClose={() => setConfirmUnsync(false)}
        onConfirm={unsyncEquipment}
        title="Unsync equipment?"
        message="This will DELETE every equipment row created from this document. The document itself will remain."
        confirmLabel="Unsync equipment"
        testId={`manual-equipment-unsync-confirm-${manualDoc.id}`}
        busy={equipBusy}
      />
    </div>
  );
}

/* Iter 290zp.14 — Equipment chip on manual invoice / credit_note rows. */
function EquipmentChip({ applied, count, busy, hasLines, docId, onOpen, onUnsync }) {
  if (applied) {
    return (
      <button
        type="button"
        onClick={onUnsync}
        disabled={busy}
        className="inline-flex items-center gap-1 text-[10px] font-medium uppercase tracking-wider px-2 py-1 rounded-md border border-emerald-500/40 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/15 transition-colors disabled:opacity-60"
        data-testid={`manual-equipment-chip-${docId}`}
        title="Click to unsync — deletes every equipment row created from this document."
      >
        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Package className="w-3 h-3" />}
        <span>Equipment ({count}) ✓</span>
      </button>
    );
  }
  return (
    <button
      type="button"
      onClick={onOpen}
      disabled={busy || !hasLines}
      className="inline-flex items-center gap-1 text-[10px] font-medium uppercase tracking-wider px-2 py-1 rounded-md border border-white/10 text-[#a0aabf] hover:border-[#1fb6c1]/40 hover:text-[#1fb6c1] transition-colors disabled:opacity-50"
      data-testid={`manual-equipment-chip-${docId}`}
      title={hasLines ? "Promote line items to site equipment" : "Re-scan the document so line items can be extracted first."}
    >
      {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Package className="w-3 h-3" />}
      <span>{hasLines ? "Add to equipment" : "Equipment (no lines yet)"}</span>
    </button>
  );
}

/* ──────────────────────────── OCR row ─────────────────────────────── */

function OcrRow({ doc, customerId, onChanged, onError, selected, onToggleSelect }) {
  const [expanded, setExpanded] = useState(false);
  const total = doc.totals?.total;
  const hasLines = (doc.lines || []).length > 0;
  const isProcessing = doc.status === "processing";
  const isFailed = doc.status === "failed";

  return (
    <div className={`border rounded-md overflow-hidden ${
      isProcessing ? "border-amber-400/40 bg-amber-500/5 animate-pulse" :
      isFailed ? "border-rose-400/40 bg-rose-500/5" :
      selected ? "border-[#1FB6C1]/60 bg-[#1FB6C1]/5" :
      "border-white/5 bg-white/[0.02]"
    }`} data-testid={`doc-row-${doc.id}`}>
      <div className="px-3 py-2 flex flex-col md:flex-row md:items-center gap-2">
        {/* Iter 411 — SELECT checkbox removed per user request. */}
        <button
          type="button"
          onClick={() => !isProcessing && setExpanded((v) => !v)}
          className="flex items-start gap-2 flex-1 min-w-0 text-left"
          data-testid={`doc-row-${doc.id}-expand`}
        >
          {isProcessing ? <Loader2 className="w-3 h-3 text-amber-300 mt-1 shrink-0 animate-spin" /> :
            expanded ? <ChevronDown className="w-3 h-3 text-[#647087] mt-1 shrink-0" /> :
            <ChevronRight className="w-3 h-3 text-[#647087] mt-1 shrink-0" />}
          {isProcessing ? (
            <span className="text-[9px] uppercase tracking-wider mt-1 px-1.5 py-0.5 rounded bg-amber-500/20 border border-amber-400/50 text-amber-200 shrink-0 font-mono">Processing…</span>
          ) : isFailed ? (
            <span className="text-[9px] uppercase tracking-wider mt-1 px-1.5 py-0.5 rounded bg-rose-500/20 border border-rose-400/50 text-rose-200 shrink-0 font-mono">Failed</span>
          ) : (
            <span className="text-[9px] uppercase tracking-wider mt-1 px-1.5 py-0.5 rounded bg-white/10 text-[#a0aabf] shrink-0 font-mono">Scanned</span>
          )}
          <div className="flex-1 min-w-0">
            <div className="text-sm truncate">
              <span className="font-medium">{doc.supplier || doc.bill_to?.name || (isProcessing ? "Parsing with AI…" : "—")}</span>
              {doc.invoice_number && (
                <span className="text-[#647087] font-mono text-xs ml-2">#{doc.invoice_number}</span>
              )}
            </div>
            <div className="text-[10px] text-[#647087] mt-0.5">
              {isProcessing ? "Usually ready in 20–40 seconds · you'll be notified when done" : (
                <>
                  {doc.invoice_date || (doc.created_at || "").slice(0, 10)}
                  {total != null && ` · R ${Number(total).toLocaleString('en-GB')}`}
                  {hasLines && ` · ${doc.lines.length} line${doc.lines.length !== 1 ? "s" : ""}`}
                </>
              )}
            </div>
          </div>
        </button>

        {!isProcessing && !isFailed && <RowChips doc={doc} onChanged={onChanged} onError={onError} customerId={customerId} />}
      </div>

      {expanded && <OcrDetail doc={doc} customerId={customerId} />}
    </div>
  );
}

/* ───────────────────── Row-level inline chip buttons ────────────────────── */

function RowChips({ doc, onChanged, onError, customerId }) {
  const [busy, setBusy] = useState("");
  const [confirmUnsync, setConfirmUnsync] = useState(null);
  const [jcPickerOpen, setJcPickerOpen] = useState(false);
  const [ticketPickerOpen, setTicketPickerOpen] = useState(false);

  const applicable = CHIP_MATRIX[doc.doc_type] || [];
  const hasLines = (doc.lines || []).length > 0;

  const call = async (fn, label) => {
    setBusy(label);
    onError("");
    try {
      await fn();
      await onChanged?.();
    } catch (ex) {
      onError(ex?.response?.data?.detail || "Action failed");
    } finally {
      setBusy("");
    }
  };

  const applyEquipment = () => call(async () => {
    if (!doc.site_id) {
      window.location.href = `/ocr/invoices?customer_id=${customerId}`;
      return;
    }
    await api.post(`/ocr/invoice/${doc.id}/apply`);
  }, "equipment");

  const applyStatement = () => call(async () => {
    // Iter 393b — Two-step reconciliation flow:
    //   1. GET a match preview → statement rows ↔ existing invoices/credit_notes
    //   2. Show the counts, ask user to accept the matches
    //   3. Accept endpoint stamps `statement_link_id` on each matched doc
    //   4. Legacy apply-statement is still called so `credit_balance`
    //      + account status flip through
    let matchInfo = null;
    try {
      const { data } = await api.get(`/ocr/invoice/${doc.id}/statement-match`);
      matchInfo = data;
    } catch (_e) { /* preview optional */ }
    if (matchInfo && (matchInfo.total_rows || 0) > 0) {
      const { match_count, unmatch_count, unmatched } = matchInfo;
      const summary = `${match_count} of ${matchInfo.total_rows} statement rows matched to existing docs.`
        + (unmatch_count
            ? `\n\n${unmatch_count} unmatched — you may want to upload the missing invoices/credit notes and re-run:\n`
              + (unmatched || []).slice(0, 6).map((u) => `  · ${u.row?.ref || u.row?.reference || "(no ref)"} — ${u.row?.amount || u.row?.debit || u.row?.credit || "?"}`).join("\n")
              + ((unmatched || []).length > 6 ? `\n  … and ${(unmatched || []).length - 6} more` : "")
            : "");
      // eslint-disable-next-line no-alert
      const ok = window.confirm(`${summary}\n\nAccept matches now?`);
      if (!ok) return;
      try {
        await api.post(`/ocr/invoice/${doc.id}/statement-match/accept`, {});
        toast.success(`Linked ${match_count} statement row${match_count === 1 ? "" : "s"} to invoices.`);
      } catch (e) {
        toast.error(`Match accept failed: ${e?.response?.data?.detail || e?.message || "unknown"}`);
      }
    }
    await api.post(`/ocr/invoice/${doc.id}/apply-statement`, {});
  }, "statement");

  const applyAnalysis = () => call(async () => {
    await api.post(`/ocr/invoice/${doc.id}/apply-analysis`);
  }, "analysis");

  const applyJobCard = (body) => call(async () => {
    // When the doc has no parsed lines, the apply-jobcard endpoint rejects
    // it (no materials to import). Use the link-only endpoint instead so the
    // user can still record an association between the doc and a JC.
    if (!hasLines && body && body.job_card_id && !body.create_new) {
      await api.post(`/ocr/invoice/${doc.id}/link-jobcard`, { job_card_id: body.job_card_id });
    } else {
      await api.post(`/ocr/invoice/${doc.id}/apply-jobcard`, body);
    }
    setJcPickerOpen(false);
  }, "jobcard");

  const applyTicket = (ticket_id) => call(async () => {
    await api.post(`/ocr/invoice/${doc.id}/apply-ticket`, { ticket_id });
    setTicketPickerOpen(false);
  }, "ticket");

  const unsync = (action) => call(async () => {
    await api.post(`/ocr/invoice/${doc.id}/unsync`, { action });
    setConfirmUnsync(null);
  }, `unsync-${action}`);

  const stateOf = (a) => doc.actions?.[a]?.status || "pending";

  const chips = [];
  if (applicable.includes("equipment") && hasLines) {
    chips.push(
      <ActionChip
        key="equipment"
        state={stateOf("equipment")}
        icon={Package}
        label={stateOf("equipment") === "applied" ? "Equipment ✓" : "Add to equipment"}
        onApply={applyEquipment}
        onUnsync={() => setConfirmUnsync("equipment")}
        busy={busy === "equipment"}
        testId={`doc-chip-equipment-${doc.id}`}
      />,
    );
  }
  if (applicable.includes("analysis")) {
    chips.push(
      <ActionChip
        key="analysis"
        state={stateOf("analysis")}
        icon={BarChart3}
        label={stateOf("analysis") === "applied" ? "In analysis ✓" : "Include in analysis"}
        onApply={applyAnalysis}
        onUnsync={() => setConfirmUnsync("analysis")}
        busy={busy === "analysis"}
        testId={`doc-chip-analysis-${doc.id}`}
      />,
    );
  }
  if (applicable.includes("statement")) {
    chips.push(
      <ActionChip
        key="statement"
        state={stateOf("statement")}
        icon={FileWarning}
        label={stateOf("statement") === "applied" ? "Balance applied ✓" : "Apply to balance"}
        onApply={applyStatement}
        onUnsync={() => setConfirmUnsync("statement")}
        busy={busy === "statement"}
        testId={`doc-chip-statement-${doc.id}`}
      />,
    );
  }
  // JC chip — when the doc has lines, "Add to job card" imports them as
  // materials; when there are no lines (manual quote/SO), still surface the
  // chip as a pure LINK button so users can mark the doc against an
  // existing job card. Backend's apply-jobcard rejects empty lines, so the
  // link-only path uses a separate endpoint /link-jobcard handled below.
  if (applicable.includes("jobcard")) {
    const linkedJcShort = doc.actions?.jobcard?.job_card_short_id;
    const appliedJc = stateOf("jobcard") === "applied";
    chips.push(
      <ActionChip
        key="jobcard"
        state={stateOf("jobcard")}
        icon={Wrench}
        label={
          appliedJc
            ? `JC ${linkedJcShort || ""} ✓`.trim()
            : hasLines
              ? "Add to job card"
              : "Link to job card"
        }
        onApply={() => setJcPickerOpen(true)}
        onUnsync={() => setConfirmUnsync("jobcard")}
        busy={busy === "jobcard"}
        testId={`doc-chip-jobcard-${doc.id}`}
      />,
    );
  }
  if (applicable.includes("ticket")) {
    const linkedTicketShort = doc.actions?.ticket?.ticket_short_id;
    const appliedT = stateOf("ticket") === "applied";
    chips.push(
      <ActionChip
        key="ticket"
        state={stateOf("ticket")}
        icon={LifeBuoy}
        label={appliedT ? `Ticket ${linkedTicketShort || ""} ✓`.trim() : "Link to ticket"}
        onApply={() => setTicketPickerOpen(true)}
        onUnsync={() => setConfirmUnsync("ticket")}
        busy={busy === "ticket"}
        testId={`doc-chip-ticket-${doc.id}`}
      />,
    );
  }

  return (
    <>
      <div className="flex flex-wrap gap-1 justify-start md:justify-end shrink-0">
        {chips}
      </div>

      <ConfirmModal
        open={!!confirmUnsync}
        onClose={() => setConfirmUnsync(null)}
        onConfirm={() => unsync(confirmUnsync)}
        title={`Unsync ${confirmUnsync}?`}
        confirmLabel={`Unsync ${confirmUnsync}`}
        message={
          confirmUnsync === "equipment"
            ? "This will DELETE every equipment row created from this document. The document itself will remain."
            : confirmUnsync === "statement"
            ? "This clears the statement-applied flag. The customer's balance is not automatically reverted — edit it manually if needed."
            : confirmUnsync === "analysis"
            ? "This excludes the document from sales analysis totals."
            : confirmUnsync === "jobcard"
            ? "This removes all OCR-imported materials from the linked job card. If the JC was auto-created with no other work, it will be deleted."
            : "This removes the document-link note from the ticket."
        }
        testId={`doc-unsync-confirm-${doc.id}`}
      />

      {jcPickerOpen && (
        <JobCardPicker
          customerId={customerId}
          doc={doc}
          onPick={applyJobCard}
          onClose={() => setJcPickerOpen(false)}
          busy={busy === "jobcard"}
        />
      )}
      {ticketPickerOpen && (
        <TicketPicker
          customerId={customerId}
          doc={doc}
          onPick={applyTicket}
          onClose={() => setTicketPickerOpen(false)}
          busy={busy === "ticket"}
        />
      )}
    </>
  );
}

function ActionChip({ state, icon: Icon, label, onApply, onUnsync, busy, testId }) {
  const applied = state === "applied";
  const cls = applied
    ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/15"
    : "border-white/10 text-[#a0aabf] hover:border-[#1fb6c1]/40 hover:text-[#1fb6c1]";
  return (
    <button
      type="button"
      onClick={applied ? onUnsync : onApply}
      disabled={busy}
      className={`inline-flex items-center gap-1 text-[10px] font-medium uppercase tracking-wider px-2 py-1 rounded-md border transition-colors disabled:opacity-60 ${cls}`}
      data-testid={testId}
      title={applied ? "Click to unsync" : undefined}
    >
      {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Icon className="w-3 h-3" />}
      <span>{label}</span>
    </button>
  );
}

/* ───────────────────────── Row detail panel ───────────────────────────── */

function OcrDetail({ doc, customerId }) {
  const hasLines = (doc.lines || []).length > 0;
  const isStmt = doc.doc_type === "statement";
  const stmt = doc.statement || {};
  const reconcile = doc.actions?.statement?.reconcile;

  return (
    <div className="border-t border-white/5 p-3 space-y-3 text-xs bg-black/20">
      {doc.bill_to?.name && (
        <div className="text-[#a0aabf]">
          <span className="text-[#647087]">Bill-to:</span> {doc.bill_to.name}
          {doc.bill_to.vat_number && <span className="font-mono ml-2">VAT {doc.bill_to.vat_number}</span>}
        </div>
      )}

      {isStmt && (stmt.closing_balance != null || stmt.total_due != null) && (
        <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-2 space-y-1">
          <div className="text-[10px] uppercase tracking-wider text-amber-300 flex items-center gap-1">
            <FileWarning className="w-3 h-3" /> Statement snapshot
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <KV label="Closing balance" value={stmt.closing_balance != null ? `R ${Number(stmt.closing_balance).toLocaleString('en-GB')}` : "—"} />
            <KV label="Total due" value={stmt.total_due != null ? `R ${Number(stmt.total_due).toLocaleString('en-GB')}` : "—"} />
            <KV label="Account status" value={stmt.account_status || "—"} />
            <KV label="As of" value={stmt.statement_date || "—"} />
          </div>
          {(stmt.days_30 || stmt.days_60 || stmt.days_90 || stmt.days_120_plus) && (
            <div className="grid grid-cols-4 gap-2 pt-1 border-t border-amber-500/20">
              <KV label="30d" value={stmt.days_30 != null ? `R ${stmt.days_30}` : "—"} />
              <KV label="60d" value={stmt.days_60 != null ? `R ${stmt.days_60}` : "—"} />
              <KV label="90d" value={stmt.days_90 != null ? `R ${stmt.days_90}` : "—"} />
              <KV label="120+d" value={stmt.days_120_plus != null ? `R ${stmt.days_120_plus}` : "—"} />
            </div>
          )}
        </div>
      )}

      {reconcile && (
        <div
          className={`rounded-md border p-2 ${
            Math.abs(reconcile.diff || 0) > 1
              ? "border-rose-500/40 bg-rose-500/[0.07]"
              : "border-emerald-500/40 bg-emerald-500/[0.05]"
          }`}
          data-testid={`doc-reconcile-${doc.id}`}
        >
          <div
            className="text-[10px] uppercase tracking-wider flex items-center gap-1 mb-1"
            style={{ color: Math.abs(reconcile.diff || 0) > 1 ? "#F43F5E" : "#10B981" }}
          >
            {Math.abs(reconcile.diff || 0) > 1 ? <AlertTriangle className="w-3 h-3" /> : <CheckCircle2 className="w-3 h-3" />}
            Reconcile vs. scanned invoices − credits
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <KV label="Invoices" value={`R ${reconcile.invoices_total}`} />
            <KV label="Credits" value={`R ${reconcile.credits_total}`} />
            <KV label="Net billed" value={`R ${reconcile.net_billed}`} />
            <KV label="Statement" value={reconcile.statement_balance != null ? `R ${reconcile.statement_balance}` : "—"} />
          </div>
          <div className={`text-xs font-bold mt-1 ${Math.abs(reconcile.diff || 0) > 1 ? "text-rose-300" : "text-emerald-300"}`}>
            Diff: R {reconcile.diff}
            {Math.abs(reconcile.diff || 0) > 1 && " — scanned docs don't match statement"}
          </div>
        </div>
      )}

      {hasLines && (
        <details className="text-[11px]" open>
          <summary className="text-[#647087] cursor-pointer hover:text-white">
            {doc.lines.length} extracted line{doc.lines.length !== 1 ? "s" : ""}
          </summary>
          <ul className="space-y-0.5 mt-1 pl-2 border-l border-white/5">
            {doc.lines.map((l, i) => (
              <li key={i} className="text-[#a0aabf] truncate">
                {l.quantity}× <span className="text-white">{l.name || l.raw_text}</span>
                {l.unit_price != null && <span className="ml-2 font-mono text-[#647087]">R {l.unit_price}</span>}
                {l.is_service_item && <span className="ml-2 text-emerald-300">⟳</span>}
              </li>
            ))}
          </ul>
        </details>
      )}

      <a
        href={`/ocr/invoices?customer_id=${customerId}&sales_doc=${doc.source_sales_document_id || ""}&job=${doc.id}`}
        className="hv-btn hv-btn-ghost text-xs inline-flex"
        data-testid={`doc-open-fullscreen-${doc.id}`}
      >
        <ExternalLink className="w-3.5 h-3.5" /> Open full scanner
      </a>
    </div>
  );
}

/* ───────────────────────── Utilities / Pickers ────────────────────────── */

function rowHasPendingChip(doc, sectionKey) {
  const applicable = CHIP_MATRIX[sectionKey] || [];
  const hasLines = (doc.lines || []).length > 0;
  for (const a of applicable) {
    if ((a === "equipment" || a === "jobcard") && !hasLines) continue;
    if ((doc.actions?.[a]?.status || "pending") !== "applied") return true;
  }
  return false;
}

function JobCardPicker({ customerId, doc, onPick, onClose, busy, bulkCount }) {
  const [jcs, setJcs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState("existing");
  const [selected, setSelected] = useState("");
  const defaultTitle = bulkCount
    ? `Materials from ${bulkCount} scanned invoice${bulkCount === 1 ? "" : "s"}`
    : `Materials from ${doc?.supplier || "scanned invoice"} ${doc?.invoice_number || ""}`.trim();
  const [title, setTitle] = useState(defaultTitle);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/jobcards", { params: { customer_id: customerId } });
        const sorted = [...data].sort((a, b) => {
          const aOpen = !["complete", "paid", "closed", "cancelled"].includes(a.phase || "");
          const bOpen = !["complete", "paid", "closed", "cancelled"].includes(b.phase || "");
          if (aOpen !== bOpen) return aOpen ? -1 : 1;
          return (b.created_at || "").localeCompare(a.created_at || "");
        });
        setJcs(sorted);
        if (sorted.length === 0) setMode("new");
      } finally {
        setLoading(false);
      }
    })();
  }, [customerId]);

  const submit = () => {
    if (mode === "new") {
      onPick({ create_new: true, title, site_id: doc?.site_id || null });
    } else {
      if (!selected) return;
      onPick({ job_card_id: selected });
    }
  };

  return (
    <Modal open onClose={onClose} title={bulkCount ? `Add ${bulkCount} documents to a job card` : "Add to job card"} testId="jc-picker" maxWidth="max-w-lg">
      <div className="space-y-3 text-sm">
        <div className="text-xs text-[#a0aabf]">
          {bulkCount
            ? `All line items from the ${bulkCount} selected document${bulkCount === 1 ? "" : "s"} will be added as materials.`
            : `${(doc?.lines || []).length} line item${(doc?.lines || []).length !== 1 ? "s" : ""} will be added as materials.`}
        </div>
        <div className="flex gap-2 text-xs">
          <button
            type="button"
            onClick={() => setMode("existing")}
            disabled={jcs.length === 0}
            className={`flex-1 px-3 py-2 rounded-md border transition-colors ${
              mode === "existing" ? "border-cyan-400/50 bg-cyan-400/10 text-cyan-200" : "border-white/10 text-[#a0aabf] hover:border-white/20"
            } disabled:opacity-40`}
            data-testid="jc-picker-mode-existing"
          >
            Existing job card
          </button>
          <button
            type="button"
            onClick={() => setMode("new")}
            className={`flex-1 px-3 py-2 rounded-md border transition-colors ${
              mode === "new" ? "border-cyan-400/50 bg-cyan-400/10 text-cyan-200" : "border-white/10 text-[#a0aabf] hover:border-white/20"
            }`}
            data-testid="jc-picker-mode-new"
          >
            <Plus className="w-3 h-3 inline mr-1" /> New job card
          </button>
        </div>
        {mode === "existing" && (
          <div className="max-h-80 overflow-y-auto border border-white/10 rounded-md divide-y divide-white/5">
            {loading && <div className="p-4 text-xs text-[#647087] text-center"><Loader2 className="w-3 h-3 animate-spin inline mr-1" /> Loading…</div>}
            {!loading && jcs.length === 0 && <div className="p-4 text-xs text-[#647087] text-center">No job cards for this customer.</div>}
            {!loading && jcs.map((jc) => (
              <button
                key={jc.id}
                type="button"
                onClick={() => setSelected(jc.id)}
                className={`w-full text-left px-3 py-2 hover:bg-white/[0.03] transition-colors ${selected === jc.id ? "bg-cyan-400/10" : ""}`}
                data-testid={`jc-picker-item-${jc.short_id || jc.id}`}
              >
                <div className="flex items-center gap-2">
                  {selected === jc.id ? <Check className="w-3 h-3 text-cyan-300 shrink-0" /> : <EmptyCircle className="w-3 h-3 text-[#647087] shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <div className="text-xs truncate">
                      <span className="text-[#647087] font-mono mr-2">{jc.short_id || jc.id.slice(0, 6)}</span>
                      <span className="text-white">{jc.title}</span>
                    </div>
                    <div className="text-[10px] text-[#647087] mt-0.5">
                      {jc.phase} · {(jc.materials || []).length} material{(jc.materials || []).length !== 1 ? "s" : ""}
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
        {mode === "new" && (
          <div className="space-y-2">
            <label className="text-[10px] uppercase tracking-wider text-[#647087]">New job card title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full hv-input text-sm"
              data-testid="jc-picker-new-title"
            />
          </div>
        )}
        <div className="flex gap-2 justify-end pt-2">
          <button type="button" onClick={onClose} className="hv-btn hv-btn-ghost text-xs">Cancel</button>
          <button
            type="button"
            onClick={submit}
            disabled={busy || (mode === "existing" && !selected)}
            className="hv-btn hv-btn-primary text-xs"
            data-testid="jc-picker-confirm"
          >
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
            {mode === "new" ? "Create & add" : "Add to selected"}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function TicketPicker({ customerId, doc, onPick, onClose, busy, bulkCount }) {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/tickets", { params: { customer_id: customerId } });
        const sorted = [...data].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
        setTickets(sorted);
      } finally {
        setLoading(false);
      }
    })();
  }, [customerId]);

  return (
    <Modal open onClose={onClose} title={bulkCount ? `Link ${bulkCount} documents to a ticket` : "Link to ticket"} testId="ticket-picker" maxWidth="max-w-lg">
      <div className="space-y-3 text-sm">
        <div className="text-xs text-[#a0aabf]">
          {bulkCount
            ? `A note linking the ${bulkCount} selected document${bulkCount === 1 ? "" : "s"} will be appended to the ticket.`
            : <>A note linking <span className="text-white">{doc?.supplier || "this document"}</span> will be appended to the selected ticket.</>}
        </div>
        <div className="max-h-80 overflow-y-auto border border-white/10 rounded-md divide-y divide-white/5">
          {loading && <div className="p-4 text-xs text-[#647087] text-center"><Loader2 className="w-3 h-3 animate-spin inline mr-1" /> Loading…</div>}
          {!loading && tickets.length === 0 && <div className="p-4 text-xs text-[#647087] text-center">No tickets for this customer.</div>}
          {!loading && tickets.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setSelected(t.id)}
              className={`w-full text-left px-3 py-2 hover:bg-white/[0.03] transition-colors ${selected === t.id ? "bg-cyan-400/10" : ""}`}
              data-testid={`ticket-picker-item-${t.short_id || t.id}`}
            >
              <div className="flex items-center gap-2">
                {selected === t.id ? <Check className="w-3 h-3 text-cyan-300 shrink-0" /> : <EmptyCircle className="w-3 h-3 text-[#647087] shrink-0" />}
                <div className="flex-1 min-w-0">
                  <div className="text-xs truncate">
                    <span className="text-[#647087] font-mono mr-2">{t.short_id || t.id.slice(0, 6)}</span>
                    <span className="text-white">{t.subject}</span>
                  </div>
                  <div className="text-[10px] text-[#647087] mt-0.5">{t.status} · {t.priority}</div>
                </div>
              </div>
            </button>
          ))}
        </div>
        <div className="flex gap-2 justify-end pt-2">
          <button type="button" onClick={onClose} className="hv-btn hv-btn-ghost text-xs">Cancel</button>
          <button
            type="button"
            onClick={() => onPick(selected)}
            disabled={busy || !selected}
            className="hv-btn hv-btn-primary text-xs"
            data-testid="ticket-picker-confirm"
          >
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
            Link document
          </button>
        </div>
      </div>
    </Modal>
  );
}

function EmptyCircle({ className = "" }) {
  return <svg className={className} viewBox="0 0 12 12" fill="none"><circle cx="6" cy="6" r="5" stroke="currentColor" strokeWidth="1" /></svg>;
}

function KV({ label, value }) {
  return (
    <div>
      <div className="text-[9px] uppercase tracking-wider text-[#647087]">{label}</div>
      <div className="text-xs font-mono">{value}</div>
    </div>
  );
}
